import React from "react";
import { FullQuestionPOJO, questionId } from "../../../models/Survey";
import EATextField from "../../shared/TextField/EATextField";
import EASelect from "../../shared/EASelect/EASelect";
import EARadio from "../../shared/Radio/EARadio";
import EACheckbox from "../../shared/Checkbox/EACheckbox";
import AccountSelect from "./components/AccountSelect";

interface IProps {
  question: FullQuestionPOJO;
  value: string;
  onChange: (questionId: string, value: string) => void;
  hasError?: boolean;
}

const SurveyQuestion: React.FC<IProps> = ({ question, value, onChange, hasError = false }) => {
  const qid = questionId(question);
  const handleChange = (val: string) => onChange(qid, val);
  const helperText = question.description ?? "";
  const placeholder = "";

  switch (question.answerType) {
    case "Text":
      return (
        <EATextField
          fullWidth
          name={qid}
          label={question.question}
          value={value}
          placeholder={placeholder}
          helperText={helperText}
          error={hasError}
          onChange={(e) => handleChange(e.target.value)}
        />
      );

    case "TextArea":
      return (
        <div className="survey-question__textarea-wrapper">
          <label className="custom-text-field__label" htmlFor={qid}>
            {question.question}
          </label>
          <div className={`custom-text-field__input-wrapper ${hasError ? "error" : ""}`}>
            <textarea
              id={qid}
              name={qid}
              className="custom-text-field__input survey-question__textarea"
              value={value}
              placeholder={placeholder}
              maxLength={undefined}
              onChange={(e) => handleChange(e.target.value)}
            />
          </div>
{helperText && (
  <p
    className={`custom-text-field__helper-text ${hasError ? "error" : ""}`}
    dangerouslySetInnerHTML={{ __html: helperText }}
  />
)}        </div>
      );

    case "Radio":
      return (
        <div className={`survey-question__radio-group ${hasError ? "survey-question__radio-group--error" : ""}`}>
          <p className="survey-question__radio-label label">{question.question}</p>
          {question.options?.map((option) => {
            const optId = questionId(option);
            return (
              <EARadio
                key={optId}
                name={qid}
                value={optId}
                checked={value === optId}
                onChange={handleChange}
                label={option.answer}
              />
            );
          })}
        {helperText && (
  <p
    className={`custom-text-field__helper-text ${hasError ? "error" : ""}`}
    dangerouslySetInnerHTML={{ __html: helperText }}
  />
)}
        </div>
      );

    case "CheckBox": {
      const hasOptions = question.options && question.options.length > 0;
      if (hasOptions) {
        const selected = value ? value.split(";") : [];
        const toggleOption = (answer: string, checked: boolean) => {
          const next = checked ? [...selected, answer] : selected.filter((a) => a !== answer);
          handleChange(next.join(";"));
        };
        return (
          <div className={`survey-question__checkbox-group ${hasError ? "survey-question__checkbox-group--error" : ""}`}>
            <p className="survey-question__radio-label label">{question.question}</p>
            {question.options.map((option) => {
              const optId = questionId(option);
              return (
                <EACheckbox
                  key={optId}
                  name={`${qid}_${optId}`}
                  checked={selected.includes(option.answer)}
                  onChange={(checked) => toggleOption(option.answer, checked)}
                  label={option.answer}
                />
              );
            })}
         {helperText && (
  <p
    className={`custom-text-field__helper-text ${hasError ? "error" : ""}`}
    dangerouslySetInnerHTML={{ __html: helperText }}
  />
)}
          </div>
        );
      }
      return (
        <div className={`survey-question__checkbox-wrapper ${hasError ? "survey-question__checkbox-wrapper--error" : ""}`}>
          <EACheckbox
            name={qid}
            checked={value === "true"}
            onChange={(checked) => handleChange(checked ? "true" : "")}
            label={question.question}
          />
        {helperText && (
  <p
    className={`custom-text-field__helper-text ${hasError ? "error" : ""}`}
    dangerouslySetInnerHTML={{ __html: helperText }}
  />
)}
        </div>
      );
    }

    case "Select":
      return (
        <EASelect
          fullWidth
          name={qid}
          label={question.question}
          value={value}
          helperText={helperText}
          placeholder={placeholder || "Wybierz opcję..."}
          options={question.options?.map((opt) => ({
            value: questionId(opt),
            label: opt.answer,
          })) ?? []}
          error={hasError}
          onChange={(e) => handleChange(e.target.value)}
        />
      );

    case "AccountIds":
      return (
        <AccountSelect
          question={question}
          qid={qid}
          value={value}
          onChange={handleChange}
          hasError={hasError}
          helperText={helperText}
        />
      );

    default:
      return null;
  }
};

export default SurveyQuestion;
